import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, ShoppingCart, RefreshCw, Download, Send, CheckCircle, XCircle, Eye, Mail, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PurchaseOrders() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [emailForm, setEmailForm] = useState({ email: '', cc: '' });
  const [newOrder, setNewOrder] = useState({
    vendor_id: '', order_date: new Date().toISOString().split('T')[0], expected_date: '',
    items: [{ description: '', hsn_code: '', quantity: 1, unit_price: 0 }], notes: ''
  });

  const isAdmin = user?.roles?.includes('admin') || user?.role === 'admin';

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordRes, vendRes] = await Promise.all([
        fetch(`${API}/api/purchases/orders`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/purchases/vendors`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (ordRes.ok) setOrders((await ordRes.json()).orders || []);
      if (vendRes.ok) setVendors((await vendRes.json()).vendors || []);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const createOrder = async () => {
    if (!newOrder.vendor_id || !newOrder.items[0].description) { toast.error('Fill required fields'); return; }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/purchases/orders`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newOrder)
      });
      if (res.ok) { 
        toast.success('PO created'); 
        setShowNew(false); 
        setNewOrder({
          vendor_id: '', order_date: new Date().toISOString().split('T')[0], expected_date: '',
          items: [{ description: '', hsn_code: '', quantity: 1, unit_price: 0 }], notes: ''
        });
        fetchData(); 
      }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  const submitForApproval = async (orderId) => {
    try {
      const res = await fetch(`${API}/api/purchases/orders/${orderId}/submit-approval`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Submitted for approval'); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
  };

  const approveOrder = async (orderId) => {
    try {
      const res = await fetch(`${API}/api/purchases/orders/${orderId}/approve`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('PO approved'); fetchData(); setShowDetail(false); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
  };

  const rejectOrder = async (orderId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      const res = await fetch(`${API}/api/purchases/orders/${orderId}/reject`, {
        method: 'POST', 
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      if (res.ok) { toast.success('PO rejected'); fetchData(); setShowDetail(false); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
  };

  const downloadPDF = async (orderId, poNumber) => {
    try {
      const res = await fetch(`${API}/api/purchases/orders/${orderId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PO_${poNumber.replace(/\//g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Download failed');
      }
    } catch (error) { toast.error('Download failed'); }
  };

  const openEmailDialog = (order) => {
    const vendor = vendors.find(v => v.vendor_id === order.vendor_id);
    setSelectedOrder(order);
    setEmailForm({ email: vendor?.email || '', cc: '' });
    setShowEmailDialog(true);
  };

  const sendEmail = async () => {
    if (!emailForm.email) { toast.error('Please enter email address'); return; }
    setProcessing(true);
    try {
      const ccList = emailForm.cc ? emailForm.cc.split(',').map(e => e.trim()).filter(e => e) : [];
      const res = await fetch(`${API}/api/purchases/orders/${selectedOrder.order_id}/send-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailForm.email, cc: ccList })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowEmailDialog(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send email');
      }
    } catch (error) { toast.error('Failed to send email'); }
    finally { setProcessing(false); }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700',
      pending_approval: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      sent: 'bg-blue-100 text-blue-700',
      confirmed: 'bg-purple-100 text-purple-700',
      received: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      pending_approval: 'PENDING APPROVAL',
      draft: 'DRAFT',
      approved: 'APPROVED',
      rejected: 'REJECTED',
      sent: 'SENT',
      confirmed: 'CONFIRMED',
      received: 'RECEIVED'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{labels[status] || status?.toUpperCase()}</Badge>;
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amt || 0);

  const filteredOrders = statusFilter === 'all' ? orders : orders.filter(o => o.status === statusFilter);
  const pendingCount = orders.filter(o => o.status === 'pending_approval').length;
  const draftCount = orders.filter(o => o.status === 'draft').length;

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <CardTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" />Purchase Orders</CardTitle>
            {pendingCount > 0 && (
              <Badge className="bg-yellow-500 text-white animate-pulse">{pendingCount} Pending Approval</Badge>
            )}
            {draftCount > 0 && (
              <Badge variant="secondary">{draftCount} Draft</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="received">Received</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchData} variant="outline" size="icon"><RefreshCw className="h-4 w-4" /></Button>
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New PO</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create Purchase Order</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div><Label>Vendor *</Label><Select value={newOrder.vendor_id} onValueChange={(v) => setNewOrder({ ...newOrder, vendor_id: v })}><SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger><SelectContent>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.company_name}</SelectItem>)}</SelectContent></Select></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Order Date</Label><Input type="date" value={newOrder.order_date} onChange={(e) => setNewOrder({ ...newOrder, order_date: e.target.value })} /></div>
                    <div><Label>Expected Date</Label><Input type="date" value={newOrder.expected_date} onChange={(e) => setNewOrder({ ...newOrder, expected_date: e.target.value })} /></div>
                  </div>
                  <div className="border rounded p-3 bg-slate-50">
                    <Label className="text-sm font-medium">Line Item</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div><Label className="text-xs">Description *</Label><Input value={newOrder.items[0].description} onChange={(e) => setNewOrder({ ...newOrder, items: [{ ...newOrder.items[0], description: e.target.value }] })} /></div>
                      <div><Label className="text-xs">HSN</Label><Input value={newOrder.items[0].hsn_code} onChange={(e) => setNewOrder({ ...newOrder, items: [{ ...newOrder.items[0], hsn_code: e.target.value }] })} /></div>
                      <div><Label className="text-xs">Qty</Label><Input type="number" min="1" value={newOrder.items[0].quantity} onChange={(e) => setNewOrder({ ...newOrder, items: [{ ...newOrder.items[0], quantity: parseInt(e.target.value) || 1 }] })} /></div>
                      <div><Label className="text-xs">Unit Price</Label><Input type="number" min="0" value={newOrder.items[0].unit_price} onChange={(e) => setNewOrder({ ...newOrder, items: [{ ...newOrder.items[0], unit_price: parseFloat(e.target.value) || 0 }] })} /></div>
                    </div>
                  </div>
                  <div><Label>Notes</Label><Textarea value={newOrder.notes} onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })} rows={2} /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button><Button onClick={createOrder} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Create PO</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Pending Approval Alert */}
        {pendingCount > 0 && isAdmin && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <span className="text-yellow-800 font-medium">{pendingCount} Purchase Order(s) awaiting your approval</span>
            <Button size="sm" variant="outline" className="ml-auto" onClick={() => setStatusFilter('pending_approval')}>
              View Pending
            </Button>
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PO #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredOrders.map((o) => (
              <TableRow key={o.order_id} className={o.status === 'pending_approval' ? 'bg-yellow-50' : ''}>
                <TableCell className="font-medium">{o.po_number}</TableCell>
                <TableCell>{o.order_date}</TableCell>
                <TableCell>{o.vendor_name}</TableCell>
                <TableCell className="text-right">{formatCurrency(o.total_amount || o.total)}</TableCell>
                <TableCell>{getStatusBadge(o.status)}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedOrder(o); setShowDetail(true); }} title="View Details">
                      <Eye className="h-4 w-4" />
                    </Button>
                    {o.status === 'draft' && (
                      <Button variant="ghost" size="sm" onClick={() => submitForApproval(o.order_id)} title="Submit for Approval">
                        <Send className="h-4 w-4 text-blue-600" />
                      </Button>
                    )}
                    {o.status === 'pending_approval' && isAdmin && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => approveOrder(o.order_id)} title="Approve">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => rejectOrder(o.order_id)} title="Reject">
                          <XCircle className="h-4 w-4 text-red-600" />
                        </Button>
                      </>
                    )}
                    {['approved', 'sent', 'confirmed', 'received'].includes(o.status) && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => downloadPDF(o.order_id, o.po_number)} title="Download PDF">
                          <Download className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEmailDialog(o)} title="Send Email">
                          <Mail className="h-4 w-4 text-blue-600" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filteredOrders.length === 0 && <div className="text-center py-8 text-slate-500">No purchase orders</div>}
      </CardContent>

      {/* Detail/Approval Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Purchase Order: {selectedOrder?.po_number}
              {selectedOrder && getStatusBadge(selectedOrder.status)}
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Vendor:</span> <strong>{selectedOrder.vendor_name}</strong></div>
                <div><span className="text-slate-500">Date:</span> <strong>{selectedOrder.order_date}</strong></div>
                <div><span className="text-slate-500">Expected:</span> <strong>{selectedOrder.expected_date || '-'}</strong></div>
                <div><span className="text-slate-500">Created By:</span> <strong>{selectedOrder.created_by}</strong></div>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Description</TableHead>
                      <TableHead>HSN</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedOrder.items || []).map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.hsn_code || '-'}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.total || item.quantity * item.unit_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end">
                <div className="text-right space-y-1">
                  <div className="text-sm"><span className="text-slate-500">Subtotal:</span> {formatCurrency(selectedOrder.subtotal)}</div>
                  <div className="text-sm"><span className="text-slate-500">CGST:</span> {formatCurrency(selectedOrder.cgst_amount || selectedOrder.cgst)}</div>
                  <div className="text-sm"><span className="text-slate-500">SGST:</span> {formatCurrency(selectedOrder.sgst_amount || selectedOrder.sgst)}</div>
                  <div className="text-lg font-bold"><span className="text-slate-500">Total:</span> {formatCurrency(selectedOrder.total_amount || selectedOrder.total)}</div>
                </div>
              </div>

              {selectedOrder.notes && (
                <div className="bg-slate-50 rounded p-3">
                  <span className="text-sm text-slate-500">Notes:</span>
                  <p className="text-sm mt-1">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            {selectedOrder?.status === 'pending_approval' && isAdmin && (
              <>
                <Button variant="destructive" onClick={() => rejectOrder(selectedOrder.order_id)}>
                  <XCircle className="h-4 w-4 mr-2" />Reject
                </Button>
                <Button className="bg-green-600 hover:bg-green-700" onClick={() => approveOrder(selectedOrder.order_id)}>
                  <CheckCircle className="h-4 w-4 mr-2" />Approve
                </Button>
              </>
            )}
            {selectedOrder?.status === 'draft' && (
              <Button onClick={() => submitForApproval(selectedOrder.order_id)}>
                <Send className="h-4 w-4 mr-2" />Submit for Approval
              </Button>
            )}
            {['approved', 'sent', 'confirmed', 'received'].includes(selectedOrder?.status) && (
              <>
                <Button variant="outline" onClick={() => downloadPDF(selectedOrder.order_id, selectedOrder.po_number)}>
                  <Download className="h-4 w-4 mr-2" />Download PDF
                </Button>
                <Button onClick={() => { setShowDetail(false); openEmailDialog(selectedOrder); }}>
                  <Mail className="h-4 w-4 mr-2" />Send Email
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowDetail(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send PO via Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Recipient Email *</Label>
              <Input 
                type="email" 
                value={emailForm.email} 
                onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                placeholder="vendor@example.com"
              />
              <p className="text-xs text-slate-500 mt-1">You can edit the vendor email here</p>
            </div>
            <div>
              <Label>CC (Optional)</Label>
              <Input 
                value={emailForm.cc} 
                onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })}
                placeholder="accounts@company.com, manager@company.com"
              />
              <p className="text-xs text-slate-500 mt-1">Separate multiple emails with comma</p>
            </div>
            <div className="bg-slate-50 rounded p-3 text-sm">
              <p><strong>PO:</strong> {selectedOrder?.po_number}</p>
              <p><strong>Vendor:</strong> {selectedOrder?.vendor_name}</p>
              <p><strong>Amount:</strong> {formatCurrency(selectedOrder?.total_amount || selectedOrder?.total)}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={processing}>
              {processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
